import {
  CampaignAsset,
  RegisterForm as RegisterFormProps,
  RegisterProfileInput,
  UpdateProfileInput,
} from '@web/graphql-components'
import { RegisterForm as PageRegisterForm } from '@web/components/page-components/RegisterForm'
import SubComponent from './SubComponent'
import { graphql } from '@web/graphql-queries/gql'
import useGraphQLClient from 'apps/fe-donation/libs/hooks/useGraphQLClient'
import { useMutation, useQuery } from '@tanstack/react-query'
import {
  ProfileCategoriesQuery,
  RegisterEmailMutation,
  RegisterProfileMutation,
  UpdateProfileMutation,
} from '@web/graphql-queries/graphql'
import { usePageStore } from 'apps/fe-donation/libs/hooks/usePageStore'
import { useSearchParams } from 'next/navigation'
import { useState } from 'react'
import { RegisterFormStep } from '@web/components/page-components/RegisterForm/RegisterForm'
import { useHttpClient } from 'apps/fe-donation/libs/hooks/useHttpClient'
import { logProfileOut } from 'apps/fe-donation/libs/queries'

const profileCategoriesDocument = graphql(/* GraphQL */ `
  query ProfileCategories($campaignId: ID!) {
    profileCategories(campaignId: $campaignId) {
      id
      name
    }
  }
`)

const RegisterForm = (props: RegisterFormProps) => {
  const [error, setError] = useState('')
  const graphQLClient = useGraphQLClient()
  const campaignId = usePageStore((state) => state.campaignId)
  const charityId = usePageStore((state) => state.charityId)
  const [step, setStep] = useState(RegisterFormStep.RegisterUser)
  const [userData, setUserData] = useState<Partial<RegisterProfileInput>>({})
  const [avatarFile, setAvatarFile] = useState<File | null>(null)
  const [registerEmailRes, setRegisterEmailRes] = useState<RegisterEmailMutation['registerEmail'] | null>(null)
  const searchParams = useSearchParams()

  const { isPeerToPeerGivingNotificationEnabled } = usePageStore.getState()

  const { data: profileCategories, isPending: isLoadingProfileCategories } = useQuery({
    queryKey: ['profileCategories', campaignId],
    queryFn: () =>
      graphQLClient<ProfileCategoriesQuery>({
        document: profileCategoriesDocument,
        options: { campaignId },
      }),
    enabled: !!campaignId,
    select: (data) => data.profileCategories,
  })
  const httpClient = useHttpClient({ isRelativeUrl: true })

  const { mutateAsync: uploadAsset } = useMutation({
    mutationKey: ['uploadAsset'],
    mutationFn: ({ file, profileId }: { file: File; profileId: string }) => {
      const state = usePageStore.getState()
      const formData = new FormData()

      formData.append('file', new File([file], file.name, { type: file.type }))
      formData.append('name', file.name)
      formData.append('profileId', profileId)
      formData.append('campaignId', state.campaignId)
      formData.append('charityId', state.charityId)

      return httpClient<CampaignAsset>(`/api/profile/assets`, {
        method: 'POST',
        body: formData,
      })
    },
  })
  const { mutateAsync: updateProfile } = useMutation({
    mutationFn: (input: UpdateProfileInput) => {
      const state = usePageStore.getState()
      if (!state.actions.getHasLoggedIn()) {
        return Promise.reject()
      }

      return httpClient<UpdateProfileMutation>('/api/profile', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ input, charityId: state.charityId }),
      })
    },
  })
  const { mutateAsync: registerProfile, isPending: isPendingRegisterProfile } = useMutation({
    mutationFn: (input: RegisterProfileInput) =>
      httpClient<RegisterProfileMutation['registerProfile']>('/api/profile', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(input),
      }),
    onSuccess: async (data) => {
      setStep(RegisterFormStep.Registered)
      if (avatarFile) {
        try {
          const { url } = await uploadAsset({ file: avatarFile, profileId: data.profileId })
          await updateProfile({
            campaignId: usePageStore.getState().campaignId,
            profileId: data.profileId,
            name: userData.profileName,
            description: userData.description,
            goal: userData.goal,
            isGivingNotificationEnabled: userData.isGivingNotificationEnabled,
            logoUrl: url,
          })
        } catch {
          // eat it
          // todo: handle
        }
      }

      if (registerEmailRes?.isExistingUser) {
        // sign out so they have to go through the log in flow again
        await logProfileOut()
        setStep(RegisterFormStep.ExistingUser)
        await httpClient('/api/profile/login', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email: userData.email, charityId, campaignId }),
        })

        return
      }

      usePageStore.getState().actions.setHasLoggedIn(true)
      window.location.assign(`${data.redirectUrl}${searchParams?.toString() ? '?' + searchParams.toString() : ''}`)
    },
    onError: (e) => {
      setError('Email already exists')
    },
  })

  const { mutateAsync: registerEmail, isPending: isPendingRegisterEmail } = useMutation({
    mutationFn: (input: { email: string }) =>
      httpClient<RegisterEmailMutation['registerEmail']>('/api/profile/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...input,
          charityId: usePageStore.getState().charityId,
          campaignId: usePageStore.getState().campaignId,
        }),
      }),
  })

  return (
    <PageRegisterForm
      {...props}
      error={error}
      SubComponent={SubComponent}
      loading={isPendingRegisterEmail || isPendingRegisterProfile || isLoadingProfileCategories}
      categories={profileCategories}
      step={step}
      isPeerToPeerGivingNotificationEnabled={isPeerToPeerGivingNotificationEnabled ?? false}
      onFileChange={(file) => setAvatarFile(file)}
      backStep={() => setStep(RegisterFormStep.RegisterUser)}
      formValues={userData}
      onSubmit={async (e) => {
        const state = usePageStore.getState()
        const formData = new FormData(e.target as HTMLFormElement)
        const data: Partial<RegisterProfileInput> = {}
        formData.forEach(
          (value, key) =>
            (data[key] = key === 'isGivingNotificationEnabled' ? (value === 'true' ? true : false) : value),
        )
        if (step === RegisterFormStep.RegisterUser) {
          const { firstName, lastName, email } = data
          setUserData({ firstName, lastName, email })
          const res = await registerEmail({ email: data.email! })
          setRegisterEmailRes(res)

          if (res?.hasProfileInCampaign) {
            setStep(RegisterFormStep.ExistingProfile)

            return
          }

          setStep(RegisterFormStep.RegisterProfile)

          return
        }

        registerProfile({
          charityId: state.charityId,
          campaignId: state.campaignId,
          profileCategoryId: data.profileCategoryId!,
          firstName: userData.firstName!,
          lastName: userData.lastName!,
          email: userData.email!,
          profileName: data.profileName!,
          description: data.description,
          goal: Number(data.goal),
          isGivingNotificationEnabled: data.isGivingNotificationEnabled ?? true,
        })

        setUserData({
          firstName: userData.firstName,
          lastName: userData.lastName,
          email: userData.email,
          profileName: data.profileName,
          description: data.description,
          isGivingNotificationEnabled: data.isGivingNotificationEnabled ?? true,
          goal: Number(data.goal),
        })
      }}
    />
  )
}

export default RegisterForm
